import {Corners, LightPosition, Point, Rectangle, Size} from "./types";
import {CanvasType} from "./caches/canvas-cache";

export const loadImage = async (image: { url?: string, base64?: string, resizeTo?: Size }): Promise<HTMLImageElement> => {
  if (image.url) {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = '*';

      img.onload = () => resolve(img);
      img.onerror = error => {
        reject(error);
      };
      img.src = image.url!;
    });
  }

  if (image.base64) {
    let base64Image = image.base64;
    if (image.resizeTo){
      base64Image = await resizeBase64Image(image.base64!, image.resizeTo)
    }

    return base64ToImage(base64Image);
  }
  throw new Error('No image url or base64 given');
};

export function calculatePerspectiveShiftProperties({doorCorners, imageSize}: {
  doorCorners: Corners,
  imageSize: Size
}): {
  doorCornersOriginal: Array<number>,
  doorCornersTransformed: Array<number>,
  textureCanvasWidth: number,
  textureCanvasHeight: number
} {
  const doorCornersOriginal = [
    0, 0,
    0, imageSize.height,
    imageSize.width, imageSize.height,
    imageSize.width, 0,
  ];

  const doorCornersTransformed = [
    doorCorners.topLeft.x, doorCorners.topLeft.y,
    doorCorners.bottomLeft.x, doorCorners.bottomLeft.y,
    doorCorners.bottomRight.x, doorCorners.bottomRight.y,
    doorCorners.topRight.x, doorCorners.topRight.y,
  ];

  // We have to be sure that the textureCanvas is big enough to contain the whole transformed door.
  // Therefore we read out the highest and rightest points of the door coordinates
  const yCoordinates = [doorCornersTransformed[1], doorCornersTransformed[3], doorCornersTransformed[5], doorCornersTransformed[7]];
  const xCoordinates = [doorCornersTransformed[0], doorCornersTransformed[2], doorCornersTransformed[4], doorCornersTransformed[6]];
  const highestPoint = Math.max(...yCoordinates);
  const rightestPoint = Math.max(...xCoordinates);

  // But this is still not enough since on desktop the hidden canvas is bigger then the transformed door
  // On mobile the transformed door is bigger then the hidden canvas
  // To account for booth cases we can just take the max of booth to be on the save side
  const textureCanvasWidth = Math.max(rightestPoint, imageSize.width);
  const textureCanvasHeight = Math.max(highestPoint, imageSize.height);

  // BUT: we need to be careful here, if the texture canvas gets to big on mobile (3000 x 3000)
  //      the drawing of the glfxTexture to the fxCanvas below failes!

  return {
    doorCornersOriginal,
    doorCornersTransformed,
    textureCanvasWidth,
    textureCanvasHeight
  }
}

export function fitImage({ size, boundary }: { size: Size, boundary: Size }): Size & { scale: number } {
  const scale = Math.min(boundary.width / size.width, boundary.height / size.height);

  return {
    width: scale * size.width,
    height: scale * size.height,
    scale
  };
}

export function calculatePositionOnCanvasAndBounds(args: { img: Size, canvas: Size, canvasBounds: Rectangle }): {
  positionInCanvas: Point,
  boundsOnScreen: Rectangle,
  newImageSize: Size & { scale: number }
} {
  const newImageSize = fitImage({
    size: {
      width: args.img.width,
      height: args.img.height
    },
    boundary: {
      width: args.canvas.width,
      height: args.canvas.height
    }
  });
  const left = args.canvas.width / 2 - newImageSize.width / 2;
  const top = args.canvas.height / 2 - newImageSize.height / 2;

  return {
    positionInCanvas: {
      x: left,
      y: top
    },
    newImageSize,
    boundsOnScreen: new Rectangle(args.canvasBounds.x + left, args.canvasBounds.y + top, newImageSize.width, newImageSize.height)
  }
}

export function getLightPositionAccordingToFlip({
                                                  lightPosition,
                                                  flipped
                                                }: {
  lightPosition: LightPosition,
  flipped: boolean
}): LightPosition {
  // If the door is not flipped we don't have to change anything
  if (!flipped) {
    return lightPosition;
  }

  // Take the opposite light position
  switch (lightPosition) {
    case LightPosition.LEFT_1:
      return LightPosition.RIGHT_1;
    case LightPosition.LEFT_2:
      return LightPosition.RIGHT_2;
    case LightPosition.RIGHT_1:
      return LightPosition.LEFT_1;
    case LightPosition.RIGHT_2:
      return LightPosition.LEFT_2;
    default:
      return LightPosition.CENTER;
  }
}

export function getUsedMemoryUsage(): string {
  const stats = (performance as unknown as {memory: {
      jsHeapSizeLimit: number;
      totalJSHeapSize: number;
      usedJSHeapSize: number;
    }}).memory;

  if (!stats) {
    return 'unknown'
  }
  const usedHeap = stats.usedJSHeapSize;
  const totalHeap = stats.totalJSHeapSize;
  const heapLimit = stats.jsHeapSizeLimit;

  return ((usedHeap / heapLimit) * 100).toFixed(2)
}

export async function base64ToImage(base64Image: string): Promise<HTMLImageElement> {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.src = `data:image/png;base64,${base64Image.replace(/^.+,/, '')}`
    image.onload = () => resolve(image);
    image.onerror = error => reject(error);
  });
}

export async function resizeBase64Image(base64: string, { width, height }: Size): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = `data:image/png;base64,${base64.replace(/^.+,/, '')}`
    img.crossOrigin = '*';

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d')!
      ctx.drawImage(img, 0, 0, width, height);

      return resolve(canvas.toDataURL('image/jpeg'))
    };
    img.onerror = error => {
      reject(error);
    };
  })
}